/**
 * Preguntas de instrumento.
 *
 * Encuesta y grupo focal solo admiten preguntas de OPCIÓN ÚNICA, y sus opciones
 * salen de la rúbrica —no son texto libre—. Una etiqueta fuera de la tabla de
 * valoración no puntúa y deja al calificador poniendo la nota a mano.
 *
 * Store Pinia: estado compartido del módulo y orquestación de sus servicios.
 * Las vistas no llaman a `http` ni a los servicios directamente; pasan por aquí.
 * Los servicios no lanzan, así que las acciones interpretan valores neutros
 * (`[]`, `null`, `false`) como fallo.
 */

import { defineStore } from 'pinia'
import type Pregunta from '@/interfaces/Preguntas'
import { getPreguntasService, getPreguntaByIdService, crearPreguntaService, actualizarPreguntaService, eliminarPreguntaService } from '@/services/PreguntasService'

/** Store `GestionPreguntas`. Ver la cabecera del archivo para el contrato del módulo. */
export const useGestionPreguntas = defineStore('GestionPreguntas', {
  state: () => ({
    preguntas: [] as Pregunta[],
    preguntaActual: null as Pregunta | null,
    urlPreguntas: `${process.env.VUE_APP_API_BASE_URL}/api/preguntas`
  }),

  actions: {
    // Listar preguntas (con filtros opcionales)
    async getPreguntas(params?: { id_instrumento?: number; id_grupo_interes?: number; banco?: boolean }) {
      const data = await getPreguntasService(this.urlPreguntas, params)
      this.preguntas = data
      return data
    },

    // Obtener pregunta por ID
    async getPreguntaById(id: number) {
      const data = await getPreguntaByIdService(this.urlPreguntas, id)
      if (data) this.preguntaActual = data
      return data
    },

    // Crear nueva pregunta
    async crearPregunta(payload: Omit<Pregunta, 'id_pregunta'>) {
      const nueva = await crearPreguntaService(this.urlPreguntas, payload)
      if (nueva) {
        this.preguntas.push(nueva)
        this.preguntaActual = nueva
      }
      return nueva
    },

    // Actualizar pregunta
    async actualizarPregunta(id: number, payload: Partial<Pregunta>) {
      const actualizada = await actualizarPreguntaService(this.urlPreguntas, id, payload)
      if (actualizada) {
        const idx = this.preguntas.findIndex(p => p.id_pregunta === id)
        if (idx !== -1) this.preguntas[idx] = actualizada
        if (this.preguntaActual?.id_pregunta === id) {
          this.preguntaActual = actualizada
        }
      }
      return actualizada
    },

    // Eliminar pregunta
    async eliminarPregunta(id: number) {
      const ok = await eliminarPreguntaService(this.urlPreguntas, id)
      if (ok) {
        this.preguntas = this.preguntas.filter(p => p.id_pregunta !== id)
        if (this.preguntaActual?.id_pregunta === id) {
          this.preguntaActual = null
        }
      }
      return ok
    }
  }
})